import {
  computed,
  getCurrentScope,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import { getStartedProgressApi } from '../api/GetStartedProgressApi'
import type {
  GetStartedPlatformProgressItem,
  GetStartedProgressContext,
  GetStartedProgressPayloadDto,
} from '../models/getStartedProgress.types'
import {
  isGetStartedProgressEvent,
  normalizeGetStartedProgress,
} from '../service/getStartedProgressService'

import { useAccount, useWorkspace } from '@/features/account'

export interface GetStartedProgressEventBus {
  on: (name: 'onboarding_task_update', handler: (payload: unknown) => void) => () => void
}

export interface GetStartedProgressSource {
  fetchProgress: (
    context: GetStartedProgressContext,
    signal?: AbortSignal,
  ) => Promise<GetStartedProgressPayloadDto>
  retry: (
    context: GetStartedProgressContext,
    command: NonNullable<GetStartedPlatformProgressItem['retryCommand']>,
    signal?: AbortSignal,
  ) => Promise<void>
}

export interface GetStartedProgressOptions {
  accountId: MaybeRefOrGetter<number | string | undefined>
  workspaceId: MaybeRefOrGetter<number | string | undefined>
  source?: GetStartedProgressSource
  bus?: GetStartedProgressEventBus | null
  debounceMs?: number
}

export interface UseGetStartedPlatformProgress {
  platforms: Readonly<Ref<GetStartedPlatformProgressItem[]>>
  loading: ComputedRef<boolean>
  error: Readonly<Ref<Error | null>>
  retryingIds: Readonly<Ref<ReadonlySet<string>>>
  refresh: () => Promise<void>
  retry: (platform: GetStartedPlatformProgressItem) => Promise<void>
  dispose: () => void
}

let installedEventBus: GetStartedProgressEventBus | null = null

/** App-composition seam: integrations never imports the shell implementation. */
export function registerGetStartedProgressEventBus(bus: GetStartedProgressEventBus): void {
  installedEventBus = bus
}

export function resetGetStartedProgressEventBusForTest(): void {
  installedEventBus = null
}

function selectedContext(options: GetStartedProgressOptions): GetStartedProgressContext | null {
  const accountId = toValue(options.accountId)
  const workspaceId = toValue(options.workspaceId)
  if (accountId === undefined || workspaceId === undefined) return null
  return { accountId, workspaceId }
}

function contextKey(context: GetStartedProgressContext): string {
  return `${context.accountId}:${context.workspaceId}`
}

export function createGetStartedPlatformProgress(
  options: GetStartedProgressOptions,
): UseGetStartedPlatformProgress {
  const source = options.source ?? getStartedProgressApi
  const platforms = ref<GetStartedPlatformProgressItem[]>([])
  const pending = ref(false)
  const error = ref<Error | null>(null)
  const retryingIds = ref<ReadonlySet<string>>(new Set())
  let generation = 0
  let request: Promise<void> | null = null
  let requestKey = ''
  let controller: AbortController | null = null
  const retryControllers = new Map<string, AbortController>()
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let unsubscribe: (() => void) | null = null
  let disposed = false

  const isCurrent = (
    candidate: GetStartedProgressContext,
    candidateGeneration: number,
  ): boolean => {
    const current = selectedContext(options)
    return (
      !disposed &&
      candidateGeneration === generation &&
      current !== null &&
      contextKey(current) === contextKey(candidate)
    )
  }

  async function refresh(): Promise<void> {
    const context = selectedContext(options)
    if (!context || disposed) return
    const key = contextKey(context)
    if (request && requestKey === key) return request

    controller?.abort()
    controller = new AbortController()
    const signal = controller.signal
    const candidateGeneration = generation
    requestKey = key
    pending.value = true
    error.value = null

    const job = source
      .fetchProgress(context, signal)
      .then((payload) => {
        if (!isCurrent(context, candidateGeneration)) return
        platforms.value = normalizeGetStartedProgress(payload, context)
      })
      .catch((cause: unknown) => {
        if (!isCurrent(context, candidateGeneration) || signal.aborted) return
        error.value = cause instanceof Error ? cause : new Error('Progress request failed')
      })
      .finally(() => {
        if (request !== job) return
        request = null
        requestKey = ''
        if (isCurrent(context, candidateGeneration)) pending.value = false
      })
    request = job
    return job
  }

  async function retry(platform: GetStartedPlatformProgressItem): Promise<void> {
    if (!platform.retryCommand || retryingIds.value.has(platform.id)) return
    const context = selectedContext(options)
    if (!context || disposed) return
    const candidateGeneration = generation
    const retryController = new AbortController()
    retryControllers.set(platform.id, retryController)
    retryingIds.value = new Set(retryingIds.value).add(platform.id)
    error.value = null
    try {
      await source.retry(context, platform.retryCommand, retryController.signal)
      if (isCurrent(context, candidateGeneration)) await refresh()
    } catch (cause: unknown) {
      if (isCurrent(context, candidateGeneration)) {
        error.value = cause instanceof Error ? cause : new Error('Progress retry failed')
      }
    } finally {
      if (isCurrent(context, candidateGeneration)) {
        const next = new Set(retryingIds.value)
        next.delete(platform.id)
        retryingIds.value = next
      }
      retryControllers.delete(platform.id)
    }
  }

  function scheduleRefresh(): void {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void refresh()
    }, options.debounceMs ?? 500)
  }

  const bus = options.bus === undefined ? installedEventBus : options.bus
  if (bus) {
    unsubscribe = bus.on('onboarding_task_update', (payload) => {
      const context = selectedContext(options)
      if (context && isGetStartedProgressEvent(payload, context.workspaceId)) scheduleRefresh()
    })
  }

  const stopContextWatch = watch(
    [() => toValue(options.accountId), () => toValue(options.workspaceId)],
    () => {
      generation += 1
      controller?.abort()
      for (const retryController of retryControllers.values()) retryController.abort()
      retryControllers.clear()
      controller = null
      request = null
      requestKey = ''
      pending.value = false
      error.value = null
      retryingIds.value = new Set()
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = null
      if (!selectedContext(options)) platforms.value = []
      else void refresh()
    },
    { immediate: true },
  )

  function dispose(): void {
    if (disposed) return
    disposed = true
    generation += 1
    stopContextWatch()
    controller?.abort()
    controller = null
    for (const retryController of retryControllers.values()) retryController.abort()
    retryControllers.clear()
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = null
    unsubscribe?.()
    unsubscribe = null
  }

  if (getCurrentScope()) onScopeDispose(dispose)

  return {
    platforms,
    loading: computed(() => pending.value),
    error,
    retryingIds,
    refresh,
    retry,
    dispose,
  }
}

export function useGetStartedPlatformProgress(): UseGetStartedPlatformProgress {
  const account = useAccount()
  const workspace = useWorkspace()
  return createGetStartedPlatformProgress({
    accountId: account.accountId,
    workspaceId: workspace.workspaceId,
  })
}
